import { useDispatch } from "react-redux";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Box, Button, Card, Flex, Label, Spinner, Switch, Text, useColorMode } from "theme-ui";
import { Inputform } from "../../forms-component/InputForm";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { createActionHighlight, editActionHighlight, getActionHighlighterIds, getActionHighlighterIdsSuccess } from "../../../store/actions/action.Highlighters";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../@types";
import { useLocation } from "react-router-dom";

export const ActionHourADay = ({ Open, setOpen, selectedHighlight,
  actionObject, hourADay, openedFrom }: any) => {

  const { actionHighlights, createHighlight, loader } = useSelector((state: any) => state.actionHighlight)
  const { auther } = useSelector((state: RootState) => state.auth);

  const shouldRestrictUser = (() => {
    const roleNames = auther?.Role?.map(r => r?.RoleName) || [];

    const restrictedRoles = ['Task Worker', 'Task Manager'];
    const higherRoles = ['Super Admin', 'Project Manager', 'Region Admin'];

    if (roleNames.some(role => higherRoles.includes(role))) {
      return false;
    } else if (
      roleNames.length > 0 &&
      roleNames.every(role => restrictedRoles.includes(role))
    ) {
      return true;
    } else {
      return false;
    }
  })();
  const dispatch = useDispatch();
  const location = useLocation();
  const hoursData = actionHighlights?.payload?.Description[0] || []
  const [colorMode] = useColorMode();
  const modalHeader = colorMode === 'light' ? "#fff" : "#20283c";
  const label = colorMode === 'light' ? "black" : "#fff";

  const togglePopover = () => {
    setOpen(!Open);
  };

  useEffect(() => {
    dispatch(getActionHighlighterIds(actionObject, { name: selectedHighlight?.name }))
  }, [createHighlight])

  useEffect(() => {
    return () => {
      dispatch(getActionHighlighterIdsSuccess([]))
    }
  }, [])

  const getInitialHoursADay = () => {
    // If creating from task level (isTaskHourADay is true) and we're on stage page
    if (actionHighlights?.isTaskHourADay === true && location.pathname.includes('/edit-stages')) {
      return hourADay || '';  // Display hourADay value at stage level
    }
    // If both stage and task are enabled, prioritize hourADay (task level value)
    else if (actionHighlights?.isStageHourADay === true && actionHighlights?.isTaskHourADay === true) {
      return hourADay || '';
    }
    // If only stage level is enabled and we're on stage page, but hourADay exists (toggle scenario)
    else if (actionHighlights?.isStageHourADay === true && actionHighlights?.isTaskHourADay === false && location.pathname.includes('/edit-stages') && hoursData?.isChecked === true) {
      if (openedFrom === 'highlighter') {
        return hourADay || '';
      } else {
        return hoursData?.hoursADay || '';
      }
    }
    // If stage enabled but we're on task page
    else if (actionHighlights?.isStageHourADay === true && actionHighlights?.isTaskHourADay === false && location.pathname.includes('/edit-task')) {
      return hourADay || '';
    }
    // Default case
    else {
      return hoursData?.hoursADay || hourADay || '';
    }
  };
  const shouldDisableSwitchAndHideSend =
    !!hourADay && location.pathname.includes('/edit-task') && !actionHighlights?.isTaskHourADay;

  const initialHoursADay = getInitialHoursADay();
  const initialIsChecked = hoursData?.isChecked || false;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      hoursADay: initialHoursADay,
      isChecked: initialIsChecked,
      name: selectedHighlight?.name,
    },
    validationSchema: Yup.object({
      hoursADay: Yup.string().required('Check list item is required'),
    }),
    onSubmit: (value) => {
      if (value?.isChecked) {
        const updateData = {
          data: { isChecked: value?.isChecked, hoursADay: value?.hoursADay, },
          ...actionObject,
          actionType: selectedHighlight?.name,
          name: selectedHighlight?.name,
        }
        dispatch(editActionHighlight(updateData, actionHighlights?._id))
      } else {
        const payload = {
          actionType: selectedHighlight?.name,
          payload: {
            Description: [value],
          },
          ...actionObject,
        }
        dispatch(createActionHighlight(payload))
      }
      setOpen(false);
      formik.resetForm()
    }
  });

  const { handleSubmit, setFieldValue, values, handleBlur, touched, errors } = formik;

  // Check if user has 'Task Worker' or 'Task Manager' role
  const hasTaskWorkerOrManager = auther?.Role?.some(
    (r: any) => r?.RoleName === 'Task Worker' || r?.RoleName === 'Task Manager'
  );

  const shouldDisableInput = shouldRestrictUser && location.pathname.includes('/edit-stages');
  const closeButton = (
    <button
      type="button"
      aria-label="Close"
      onClick={togglePopover}
      style={{
        color: colorMode === 'dark' ? '#fff' : '#000',
        background: 'transparent',
        border: 'none',
        fontSize: 23,
        lineHeight: 1,
        padding: 0,
        cursor: 'pointer'
      }}
    >
      <span aria-hidden="true">&times;</span>
    </button>
  );
  return (
    <Modal isOpen={Open} centered scrollable size='md' toggle={togglePopover}>
      <ModalHeader toggle={togglePopover} close={closeButton} style={{ backgroundColor: modalHeader }}>
        <Flex sx={{ gap: "1rem" }}>
          <img src={selectedHighlight?.icon} />
          <Text sx={{ color: selectedHighlight?.color }}>
            {selectedHighlight?.name?.toUpperCase()}
          </Text>
        </Flex>
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody style={{ backgroundColor: modalHeader, color: label }}>
          <Card sx={{ boxShadow: "none", border: "1px solid #F7EFE5", borderRadius: "12px", padding: "12px 20px", position: "relative", background: modalHeader }}>
            {/* Hour Per Day input only disabled for Task Worker at Stage level */}
            <Text sx={{ color: label, fontSize: '14px', fontWeight: 500 }}>Hour Per Day</Text>
            <Inputform
              label={""}
              Placeholder={"Enter hour per day..."}
              Name={"hoursADay"}
              type='number'
              Title_sx={{ color: "grey3" }}
              touched={touched?.hoursADay}
              errors={errors?.hoursADay}
              values={values.hoursADay}
              Max_Length={10}
              onBlur={handleBlur}
              OnChange={(event: any) => {
                const newName = event.target.value;
                setFieldValue("hoursADay", newName, true);
              }}
              disabled={shouldDisableInput}
              sx={shouldDisableInput ? { backgroundColor: '#a8a8a875', color: colorMode === 'light' ? '#000' : '#fff', opacity: 1 } : {}}
            />

            <Flex sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 4,
            }}>
              <Label htmlFor="onHold" sx={{ flex: 1, color: label }}>
                Enabled
              </Label>
              <Box>
                {/* Switch always enabled */}
                <Switch
                  name="on-hold"
                  id="onHold"
                  checked={values?.isChecked ? false : true}
                  onClick={(e: any) => setFieldValue("isChecked", e.target.checked ? false : true)}
                  disabled={shouldDisableSwitchAndHideSend || shouldDisableInput}
                  sx={{ cursor: shouldDisableSwitchAndHideSend ? "not-allowed" : "pointer" }}
                />
              </Box>
            </Flex>

            {loader && (
              <Box sx={{ position: "absolute", inset: "0", width: "100%", height: "100%", backgroundColor: 'rgba(255, 255, 255, 0.5)', display: "grid", placeContent: "center" }}>
                <Spinner />
              </Box>
            )}
          </Card>
        </ModalBody>

        <ModalFooter className='gap-2 justify-content-end' style={{ backgroundColor: modalHeader }}>
          <Button
            variant='SaveButton'
            type="submit"
            style={{
              cursor:
                (Number(values.hoursADay) === Number(initialHoursADay) &&
                  values.isChecked === initialIsChecked)
                  ? "not-allowed"
                  : "pointer"
            }}
            disabled={
              Number(values.hoursADay) === Number(initialHoursADay) &&
              values.isChecked === initialIsChecked
            }
          >
            Send
          </Button>
        </ModalFooter>

      </form>
    </Modal>
  )
}
